import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Mail, Plus, X, AlertCircle, Send } from 'lucide-react';
import axiosInstance from '../../../utils/axiosConfig';
import { useAuth } from '../../../context/AuthContext';
import { tiempoRelativo } from '../utils/helpers';
import { BUTTON, FOCUS } from '../utils/uiTokens';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const fechaLarga = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const mensajeBase = (tarea, proyecto, remitente) => {
  const fecha = fechaLarga(tarea.fecha_vencimiento);
  return [
    `Te escribo por la actividad "${tarea.titulo}" del proyecto ${proyecto?.nombre || tarea.proyecto_nombre || ''}${fecha ? `, comprometida para el ${fecha}` : ''}.`,
    '',
    'Para poder avanzar necesitamos la información pendiente de tu parte. ¿Nos la puedes enviar respondiendo este correo?',
    '',
    'Gracias,',
    remitente || '',
  ].join('\n');
};

/**
 * Recordatorios por correo a terceros (proveedor, cliente, otra area) de los
 * que depende la tarea. Formulario con asunto y mensaje prellenados + historial.
 * El responsable de la tarea sigue siendo el mismo: esto solo envia y registra.
 */
const RecordatorioTercero = ({ tarea, proyecto }) => {
  const { user } = useAuth();
  const [historial, setHistorial] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [asunto, setAsunto] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [enviadoA, setEnviadoA] = useState('');

  const cargar = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/api/tareas/${tarea.id}/recordatorios-terceros`);
      setHistorial(data || []);
    } catch {
      setHistorial([]);
    }
  }, [tarea.id]);

  useEffect(() => { cargar(); }, [cargar]);

  // Destinatarios previos para reusar con un click.
  const previos = useMemo(() => {
    const vistos = new Map();
    historial.forEach((h) => {
      if (!vistos.has(h.email_destino)) vistos.set(h.email_destino, h.nombre_destino);
    });
    return Array.from(vistos.entries()).slice(0, 4);
  }, [historial]);

  const abrir = () => {
    const ultimo = historial[0];
    setEmail(ultimo?.email_destino || '');
    setNombre(ultimo?.nombre_destino || '');
    setAsunto(`Recordatorio: ${tarea.titulo} - ${proyecto?.nombre || tarea.proyecto_nombre || ''}`.slice(0, 250));
    setMensaje(mensajeBase(tarea, proyecto, user?.nombre_empleado || user?.username));
    setError('');
    setAbierto(true);
  };

  useEffect(() => {
    if (!abierto) return;
    // Esc cierra solo este modal (no el slide-over de abajo).
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setAbierto(false);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [abierto]);

  const enviar = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setError('Revisa el correo del destinatario.');
      return;
    }
    setEnviando(true);
    setError('');
    try {
      await axiosInstance.post(`/api/tareas/${tarea.id}/recordatorios-terceros`, {
        email: email.trim(),
        nombre: nombre.trim() || null,
        asunto: asunto.trim(),
        mensaje: mensaje.trim(),
      });
      setAbierto(false);
      setEnviadoA(email.trim());
      setTimeout(() => setEnviadoA(''), 4000);
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo enviar el correo.');
      cargar();
    } finally {
      setEnviando(false);
    }
  };

  const inputCls = `w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md placeholder-slate-400 ${FOCUS.input}`;

  return (
    <section>
      <div className="flex items-center gap-1.5 mb-2">
        <Mail size={11} strokeWidth={2} className="text-slate-400" />
        <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">
          Correos a terceros
        </span>
        {historial.length > 0 && (
          <span className="text-[10px] text-slate-400 tabular-nums">{historial.length}</span>
        )}
        <div className="flex-1 h-px bg-slate-200/60" />
        <button
          type="button"
          onClick={abrir}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <Plus size={10} strokeWidth={2.5} />
          Enviar recordatorio
        </button>
      </div>

      {enviadoA && (
        <p className="text-xs text-emerald-700 px-1 pb-1">Recordatorio enviado a {enviadoA}.</p>
      )}

      {historial.length === 0 ? (
        <p className="text-xs text-slate-400 italic px-1 py-1">
          Si esta tarea espera algo de un proveedor u otra área, envíale un recordatorio desde aquí.
        </p>
      ) : (
        <ul className="space-y-0.5">
          {historial.slice(0, 5).map((h) => (
            <li key={h.id} className="flex items-baseline gap-2 px-1 py-1 text-xs" title={h.asunto}>
              <span className={`truncate flex-1 ${h.ok ? 'text-slate-700' : 'text-rose-600'}`}>
                {h.nombre_destino || h.email_destino}
                {!h.ok && ' (no se envió)'}
              </span>
              <span className="text-[10px] text-slate-400 flex-shrink-0">
                {tiempoRelativo(h.enviado_en)}{h.enviado_por ? `, ${h.enviado_por.split(' ')[0]}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}

      {abierto && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setAbierto(false); }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <form
            onSubmit={enviar}
            className="w-full max-w-lg max-h-[92vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="flex items-start justify-between px-6 pt-5 pb-4">
              <div className="min-w-0">
                <h2 className="text-base font-semibold tracking-tight text-slate-900">Enviar correo recordatorio</h2>
                <p className="text-xs text-slate-500 mt-0.5 truncate">
                  {tarea.codigo} · {tarea.titulo}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAbierto(false)}
                className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 p-1.5 rounded-md transition-colors"
                aria-label="Cerrar"
              >
                <X size={16} />
              </button>
            </div>

            <div className="px-6 pb-5 space-y-4 overflow-y-auto min-h-0">
              <div className="grid grid-cols-5 gap-2">
                <div className="col-span-3">
                  <label htmlFor="rt-email" className="block text-xs font-medium text-slate-600 mb-1.5">
                    Para <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="rt-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="proveedor@empresa.com"
                    autoFocus
                    className={inputCls}
                  />
                </div>
                <div className="col-span-2">
                  <label htmlFor="rt-nombre" className="block text-xs font-medium text-slate-600 mb-1.5">
                    Nombre
                  </label>
                  <input
                    id="rt-nombre"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    placeholder="Opcional"
                    maxLength={120}
                    className={inputCls}
                  />
                </div>
              </div>

              {previos.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 -mt-2">
                  <span className="text-[11px] text-slate-400">Usados antes:</span>
                  {previos.map(([correo, nom]) => (
                    <button
                      key={correo}
                      type="button"
                      onClick={() => { setEmail(correo); setNombre(nom || ''); }}
                      className="px-2 py-0.5 rounded-md text-[11px] bg-slate-100 text-slate-600 hover:bg-slate-200"
                    >
                      {nom || correo}
                    </button>
                  ))}
                </div>
              )}

              <div>
                <label htmlFor="rt-asunto" className="block text-xs font-medium text-slate-600 mb-1.5">Asunto</label>
                <input
                  id="rt-asunto"
                  value={asunto}
                  onChange={(e) => setAsunto(e.target.value)}
                  maxLength={250}
                  className={inputCls}
                />
              </div>

              <div>
                <label htmlFor="rt-mensaje" className="block text-xs font-medium text-slate-600 mb-1.5">Mensaje</label>
                <textarea
                  id="rt-mensaje"
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value)}
                  rows={8}
                  className={`${inputCls} resize-y leading-relaxed`}
                />
                <p className="mt-1.5 text-[11px] text-slate-500">
                  El correo incluye el proyecto, la actividad y la fecha comprometida. Te llega una copia y las respuestas van
                  directo a tu correo.
                </p>
              </div>

              {error && (
                <div className="flex items-start gap-2 px-3 py-2 bg-rose-50 border border-rose-200/70 rounded-md">
                  <AlertCircle size={14} className="text-rose-500 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-rose-700">{error}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-6 py-3 bg-slate-50/60 border-t border-slate-200/70">
              <button type="button" onClick={() => setAbierto(false)} className={`${BUTTON.ghost} ${BUTTON.size.md}`}>
                Cancelar
              </button>
              <button
                type="submit"
                disabled={enviando || !email.trim() || !asunto.trim() || !mensaje.trim()}
                className={`${BUTTON.primary} ${BUTTON.size.md}`}
              >
                <Send size={13} /> {enviando ? 'Enviando...' : 'Enviar correo'}
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </section>
  );
};

export default RecordatorioTercero;
